import Loading from "@/src/components/Loading";
import {
  SettingsCard,
  SettingsPage,
  SettingsRow,
} from "@/src/components/settings/SettingsLayout";
import { toasts } from "@/src/components/toasts/toasts";
import { Switch } from "@/src/components/ui/switch";
import {
  DdqlDataRoomFolderSetting,
  FirmQuery,
  UpdateFirmDealSettingsInput,
  useFirmQuery,
  useUpdateFirmMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { authSelectors } from "@/src/store/auth/selector";
import { useQueryClient } from "@tanstack/react-query";
import {
  FolderIcon,
  LockIcon,
  MessageCircleQuestionIcon,
  ShieldIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  NavLink,
  Redirect,
  Route,
  Switch as RouterSwitch,
} from "react-router-dom";
import { DataRoomPermissions } from "./DataRoomPermissions";
import { FirmRolesSettings } from "./Roles";

const NAV_ITEMS = [
  {
    label: "Data Room",
    path: "data-room",
    icon: FolderIcon,
  },
  {
    label: "Roles",
    path: "roles",
    icon: ShieldIcon,
  },
  {
    label: "Questions",
    path: "questions",
    icon: MessageCircleQuestionIcon,
  },
  {
    label: "Access",
    path: "access",
    icon: LockIcon,
  },
] as const;

export function FirmDealSettings() {
  const account = useSelector(authSelectors.account);
  const client = useGqlClient();
  const firmId = account?.firm?.id ?? "";

  const firmQuery = useFirmQuery(
    client,
    { id: firmId },
    { enabled: firmId !== "" },
  );

  if (firmQuery.error) {
    return (
      <div className="p-8">
        <p className="text-sm text-muted-foreground">Something went wrong</p>
      </div>
    );
  }

  if (firmQuery.isLoading || !firmQuery.data) {
    return (
      <div className="min-h-32 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return <FirmDealSettingsShell firm={firmQuery.data.firm} />;
}

function FirmDealSettingsShell(props: { firm: FirmQuery["firm"] }) {
  return (
    <div className="flex min-h-[28rem]">
      <aside className="w-56 shrink-0 border-r border-border py-6 px-3">
        <nav className="flex flex-col gap-0.5">
          {NAV_ITEMS.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={`/firm/deal-settings/${path}`}
              className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
              activeClassName="!bg-muted !text-foreground"
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-8">
          <RouterSwitch>
            <Route path="/firm/deal-settings" exact>
              <Redirect to="/firm/deal-settings/data-room" />
            </Route>
            <Route path="/firm/deal-settings/data-room">
              <DataRoomSettings firm={props.firm} />
            </Route>
            <Route path="/firm/deal-settings/roles">
              <FirmRolesSettings
                firmId={props.firm.id}
                roles={props.firm.dealRoles}
              />
            </Route>
            <Route path="/firm/deal-settings/questions">
              <QuestionsSettings firm={props.firm} />
            </Route>
            <Route path="/firm/deal-settings/access">
              <AccessSettings firm={props.firm} />
            </Route>
          </RouterSwitch>
        </div>
      </div>
    </div>
  );
}

function useFirmDealSettingsUpdater(firmId: string) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateFirm = useUpdateFirmMutation(client);

  function updateDealSettings(dealSettings: UpdateFirmDealSettingsInput) {
    if (updateFirm.isPending) {
      return;
    }

    updateFirm.mutate(
      {
        input: {
          id: firmId,
          dealSettings,
        },
      },
      {
        onSuccess: () => {
          toasts.success("Settings updated");
          queryClient.invalidateQueries({
            queryKey: ["Firm", { id: firmId }],
          });
        },
        onError: () => {
          toasts.error("Failed to update settings");
          queryClient.invalidateQueries({
            queryKey: ["Firm", { id: firmId }],
          });
        },
      },
    );
  }

  return { updateDealSettings, isPending: updateFirm.isPending };
}

function DataRoomSettings(props: { firm: FirmQuery["firm"] }) {
  const { updateDealSettings, isPending } = useFirmDealSettingsUpdater(
    props.firm.id,
  );
  const [indexingEnabled, setIndexingEnabled] = useState(
    props.firm.settings.dealSettings.indexingEnabled,
  );

  useEffect(() => {
    setIndexingEnabled(props.firm.settings.dealSettings.indexingEnabled);
  }, [props.firm.settings.dealSettings.indexingEnabled]);

  return (
    <SettingsPage
      title="Data Room"
      description="Default Data Room settings for new deals."
    >
      <SettingsCard title="General">
        <SettingsRow className="items-center">
          <div className="space-y-1 min-w-0 pr-4">
            <label
              htmlFor="default-indexing"
              className="text-sm font-medium text-foreground"
            >
              Indexing
            </label>
            <p className="text-sm text-muted-foreground">
              When enabled, new deals have indexing on by default.
            </p>
          </div>
          <Switch
            id="default-indexing"
            checked={indexingEnabled}
            disabled={isPending}
            onCheckedChange={(checked) => {
              setIndexingEnabled(checked);
              updateDealSettings({ indexingEnabled: checked });
            }}
          />
        </SettingsRow>
      </SettingsCard>

      <DataRoomPermissions firm={props.firm} />
    </SettingsPage>
  );
}

function QuestionsSettings(props: { firm: FirmQuery["firm"] }) {
  const { updateDealSettings, isPending } = useFirmDealSettingsUpdater(
    props.firm.id,
  );
  const [requireFolder, setRequireFolder] = useState(
    props.firm.settings.dealSettings.ddqlDataRoomFolder ===
      DdqlDataRoomFolderSetting.Required,
  );

  useEffect(() => {
    setRequireFolder(
      props.firm.settings.dealSettings.ddqlDataRoomFolder ===
        DdqlDataRoomFolderSetting.Required,
    );
  }, [props.firm.settings.dealSettings.ddqlDataRoomFolder]);

  return (
    <SettingsPage
      title="Questions"
      description="Default question list settings for new deals."
    >
      <SettingsCard title="Question lists">
        <SettingsRow className="items-center">
          <div className="space-y-1 min-w-0 pr-4">
            <label
              htmlFor="require-data-room-folder"
              className="text-sm font-medium text-foreground"
            >
              Require Data Room folder
            </label>
            <p className="text-sm text-muted-foreground">
              Require linking each question list to a Data Room folder so
              uploads are included in the Data Room.
            </p>
          </div>
          <Switch
            id="require-data-room-folder"
            checked={requireFolder}
            disabled={isPending}
            onCheckedChange={(checked) => {
              setRequireFolder(checked);
              updateDealSettings({
                ddqlDataRoomFolder: checked
                  ? DdqlDataRoomFolderSetting.Required
                  : DdqlDataRoomFolderSetting.Optional,
              });
            }}
          />
        </SettingsRow>
      </SettingsCard>
    </SettingsPage>
  );
}

function AccessSettings(props: { firm: FirmQuery["firm"] }) {
  const { updateDealSettings, isPending } = useFirmDealSettingsUpdater(
    props.firm.id,
  );
  const [autoAdmit, setAutoAdmit] = useState(
    props.firm.settings.dealSettings.accessAutoAdmit,
  );
  const [requireGuestMfa, setRequireGuestMfa] = useState(
    props.firm.settings.dealSettings.accessRequireGuestMfa,
  );

  useEffect(() => {
    setAutoAdmit(props.firm.settings.dealSettings.accessAutoAdmit);
    setRequireGuestMfa(props.firm.settings.dealSettings.accessRequireGuestMfa);
  }, [
    props.firm.settings.dealSettings.accessAutoAdmit,
    props.firm.settings.dealSettings.accessRequireGuestMfa,
  ]);

  return (
    <SettingsPage
      title="Access"
      description="Default guest access settings for new deals."
    >
      <SettingsCard
        title="Guests"
        contentClassName="divide-y divide-border p-0"
      >
        <SettingsRow className="items-center">
          <div className="space-y-1 min-w-0 pr-4">
            <label
              htmlFor="access-auto-admit"
              className="text-sm font-medium text-foreground"
            >
              Auto admit guests
            </label>
            <p className="text-sm text-muted-foreground">
              Automatically grant deal access when a guest accepts an invite.
            </p>
          </div>
          <Switch
            id="access-auto-admit"
            checked={autoAdmit}
            disabled={isPending}
            onCheckedChange={(checked) => {
              setAutoAdmit(checked);
              updateDealSettings({ accessAutoAdmit: checked });
            }}
          />
        </SettingsRow>
        <SettingsRow className="items-center">
          <div className="space-y-1 min-w-0 pr-4">
            <label
              htmlFor="access-require-guest-mfa"
              className="text-sm font-medium text-foreground"
            >
              Require guests to enable MFA
            </label>
            <p className="text-sm text-muted-foreground">
              Guests cannot access deal information until they set up MFA.
            </p>
          </div>
          <Switch
            id="access-require-guest-mfa"
            checked={requireGuestMfa}
            disabled={isPending}
            onCheckedChange={(checked) => {
              setRequireGuestMfa(checked);
              updateDealSettings({ accessRequireGuestMfa: checked });
            }}
          />
        </SettingsRow>
      </SettingsCard>
    </SettingsPage>
  );
}
